import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useLanguage } from "@/context/AppProviders";
import { api, formatApiError } from "@/lib/api";
import { FadeIn, Overline } from "@/components/Kinetic";
import { Eye, EyeOff, CheckCircle2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError(t.auth.resetMismatch);
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full bg-transparent border border-ink/20 focus:border-ember outline-none px-4 py-3.5 text-ink placeholder:text-ink/30 transition-colors duration-300 text-sm";

  return (
    <div className="min-h-screen flex items-center justify-center px-6 pt-20" data-testid="reset-password-page">
      <FadeIn className="w-full max-w-md">
        {done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 size={48} className="text-ember mx-auto" />
            <h1 className="mt-8 font-display font-semibold tracking-tighter text-4xl text-ink">{t.auth.resetSuccessTitle}</h1>
            <p className="mt-4 text-ink/55 text-sm">{t.auth.resetSuccessSub}</p>
            <button onClick={() => navigate("/auth")} data-testid="reset-goto-login" className="btn-glow mt-8 bg-ink hover:bg-ember text-white px-8 py-4 text-sm tracking-wide transition-colors duration-300">
              {t.auth.loginBtn}
            </button>
          </div>
        ) : (
          <>
            <Overline>{t.auth.resetTitle}</Overline>
            <h1 className="mt-4 font-display font-semibold tracking-tighter text-4xl text-ink">{t.auth.resetTitle}</h1>
            <p className="mt-4 text-ink/55 text-sm">{t.auth.resetSub}</p>

            {!token ? (
              <p className="mt-8 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-600" data-testid="reset-no-token">{t.auth.resetInvalid}</p>
            ) : (
              <form onSubmit={submit} className="mt-10 space-y-5" data-testid="reset-form">
                {[t.auth.newPassword, t.auth.confirmPassword].map((label, i) => (
                  <div key={i}>
                    <label className="block text-xs uppercase tracking-[0.2em] text-ink/50 mb-2">{label}</label>
                    <div className="relative">
                      <input
                        data-testid={i === 0 ? "reset-password-input" : "reset-confirm-input"}
                        type={showPassword ? "text" : "password"}
                        value={i === 0 ? password : confirm}
                        onChange={(e) => (i === 0 ? setPassword(e.target.value) : setConfirm(e.target.value))}
                        required
                        minLength={6}
                        className={`${inputCls} pr-12`}
                      />
                      {i === 0 && (
                        <button
                          type="button"
                          data-testid="reset-password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-4 top-1/2 -translate-y-1/2 text-ink/40 hover:text-ember transition-colors"
                          aria-label={showPassword ? t.auth.hidePassword : t.auth.showPassword}
                        >
                          {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                        </button>
                      )}
                    </div>
                  </div>
                ))}
                {error && <p className="text-sm text-red-600" data-testid="reset-error">{error}</p>}
                <button data-testid="reset-submit-btn" disabled={loading} className="btn-glow w-full bg-ink hover:bg-ember disabled:opacity-50 text-white px-6 py-4 text-sm tracking-wide transition-colors duration-300">
                  {loading ? t.auth.loading : t.auth.resetBtn}
                </button>
              </form>
            )}
          </>
        )}
        <Link to="/auth" className="link-line mt-8 inline-block text-sm text-ink/60 hover:text-ink transition-colors">{t.auth.toLogin}</Link>
      </FadeIn>
    </div>
  );
}
